import { Button, Flex, Skeleton } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { FinancialData } from '@finley-technologies/finley-types';

import { useGetFinancialStatementDataByDateRangeQuery } from '../../../packages/financial-data/requests/financialData.gql.generated';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { FinancialCovenantsContainer } from '~/packages/borrowing-base/ui/FinancialCovenantsContainer';
import {
  InsightsTemplate,
  TAB_OPTION_KEYS,
} from '~/packages/borrowing-base/ui/InsightsTemplate';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';
import { FinancialStatementsContainer } from '~/packages/financial-data/ui/financialStatements/FinancialStatementsContainer';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';

const FinancialViewsIterable = ['Financial Covenants', 'Financial Statements'];
type FinancialViewsTypes = typeof FinancialViewsIterable[number];

const INITIAL_FINANCIAL_STATEMENT = 'balanceSheet';
const INITIAL_REPORT_FREQUENCY = 'month' as FinancialData.ReportFrequency;

const RouteBase = () => {
  const flags = useFeatureFlags();
  const { creditFacilityId } = useCreditFacilityContext();
  const [
    selectedFinancialView,
    setSelectedFinancialView,
  ] = useState<FinancialViewsTypes>('Financial Covenants');

  const [availableFinancialViews, setAvailableFinancialViews] = useState<
    FinancialViewsTypes[]
  >(FinancialViewsIterable);

  const {
    error,
    data: financialStatementQueryResult,
    loading: financialStatementQueryLoading,
  } = useGetFinancialStatementDataByDateRangeQuery({
    variables: {
      startDate: commonDate.dateToFullDateString(chartsInitialStartDateJs),
      endDate: commonDate.dateToFullDateString(chartsInitialEndDateJs),
      financialStatement: INITIAL_FINANCIAL_STATEMENT,
      reportFrequency: INITIAL_REPORT_FREQUENCY,
      creditFacilityId,
    },
  });

  useEffect(() => {
    if (financialStatementQueryResult) {
      const financialStatementData =
        financialStatementQueryResult.getFinancialStatementDataByDateRange;

      setAvailableFinancialViews(
        financialStatementData !== null
          ? ['Financial Covenants', 'Financial Statements']
          : ['Financial Covenants'],
      );
    }
  }, [financialStatementQueryResult]);

  if (error) throw error;

  return (
    <InsightsTemplate selectedTab={TAB_OPTION_KEYS.financials}>
      {financialStatementQueryLoading ? (
        <Skeleton height="600px" />
      ) : (
        <Flex direction="column" gap="2.5rem">
          {flags.isFinancialStatementsDashboardEnabled && (
            <Flex gap="0.5rem">
              {availableFinancialViews.map((financialView) => (
                <Button
                  key={financialView}
                  variant="primary-muted"
                  colorScheme="gray"
                  isActive={selectedFinancialView === financialView}
                  onClick={() => setSelectedFinancialView(financialView)}
                >
                  {financialView}
                </Button>
              ))}
            </Flex>
          )}
          {selectedFinancialView === 'Financial Covenants' && (
            <FinancialCovenantsContainer />
          )}
          {selectedFinancialView === 'Financial Statements' &&
            flags.isFinancialStatementsDashboardEnabled && ( // @haghanim remove once we go live w/ financial statements
              <FinancialStatementsContainer
                creditFacilityId={creditFacilityId}
              />
            )}
        </Flex>
      )}
    </InsightsTemplate>
  );
};

export const InsightsFinancialsRoute = () => {
  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/insights/funding-overview" />;
      }}
    >
      <RouteBase />
    </RouteErrorBoundary>
  );
};
